import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MenuItem, PrimeIcons, SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, Subscription, forkJoin, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FormationCementCatalog } from 'src/app/catalogs/models/formations-cement-catalog.model';
import { FormationsService } from 'src/app/catalogs/shared/services/formations-cement-catalog.service';
import { FormationTop } from 'src/app/perical/models/formation.model';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { UdtService } from 'src/app/shared/services/udt.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { FormationTopService } from '../../services/formation-top.service';
import { WellTypeService } from '../../services/well-type-datums.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { Utilities } from 'src/app/core/services/utilities';
import { Store } from '@ngneat/elf';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { FormationTopsUi } from '../../models/formation-tops.model';
import { isEqual, map as map1} from "lodash-es";
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { valuesIncreaseValidator } from 'src/app/shared/components/ng-table-grid/shared-grid.validators';

@Component({
    selector: 'app-formation-tops',
    templateUrl: './formation-tops.component.html',
    styles: [``],
    providers: [DialogService, ConfirmationService],
    standalone: false
})
export class FormationTopsComponent implements OnInit, OnDestroy {

  public uu: UserUnitsModel;
  public columnDefs: any[];
  public tableHeight: string = '600px';
  public _formations: FormationCementCatalog[];
  private _subscriptions: Subscription = new Subscription();
  public formationTopsForm: UntypedFormGroup;
  public tablePlot: SelectItem[];
  private _udtPlotData: any;
  public northingUdt: SelectItem[];
  public contextMenuItems = signal<MenuItem[]>([]);

  public plot = {
    data: [],
    layout: {},
    config: {}
  };

  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotName: string;
  public downloadPlotName = 'formation_tops_plot';
  public userRoles: UserRoles;

  private defaultFormation: FormationCementCatalog;
  private _formationTops: FormationTop[]
  private _depths: number[];
  private _northing: number[];
  
  get formationTops(): UntypedFormArray {
    return this.formationTopsForm.get("formationTops") as UntypedFormArray;
  }
  
  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;
  
  // Validation
  public isLoading = signal<boolean>(true);
  public totalDepth: number;
  public mudlineDepth: number;
  
  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.formationTopsStore = this._storeService.createStore(this._componentId, new FormationTopsUi, withUIEntities<XyLinePlotUi>());
    this.tablePlotSelected = this.formationTopsStore.state.tablePlotSelected || 'table';
    this.northingUdtSelected = this.formationTopsStore.state.northingUdtSelected || 'northing';
  }
  get componentId(): string {
    return this._componentId;
  }
  public formationTopsStore: Store;

  public tablePlotSelected: string = 'table';
  public northingUdtSelected: string = 'northing';

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _wellTypeService: WellTypeService,
    private _trajectoryService: TrajectoryService,
    private _formationsService: FormationsService,
    private _formationTopService: FormationTopService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService,
    private udtService: UdtService,
    private _messenger: MediatorService,
    private _storeService: StoreService
  ) {
    this.formationTopsForm = this._formBuilder.group({
      formationTops: this._formBuilder.array([]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    this.uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.userRoles = await this._storeService.get<UserRoles>(StorageKeys.ROLES);
    
    this.columnDefs = [
      { field: 'measuredDepth', header: `Formation Top (${this.uu.longLengths}) TVD` },
      { field: 'formationTop', header: 'Formation' }
    ];

    this.tablePlot = [
      { label: 'Table', value: 'table' },
      { label: 'Plot', value: 'plot' }
    ];

    this.northingUdt = [
      { label: 'Northing', value: 'northing' },
      { label: 'UDT', value: 'udt' }
    ];

    this.plotName = 'formationTops ' + this.northingUdtSelected;

    this.getData();

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  signalRfunc(data: { action: string}) {
    if (data.action == PeriforOnChangeMessages.REFRESH_FORMATIONS_CATALOG || data.action == PeriforOnChangeMessages.REFRESH_UDT_PLOT) {
      // this.formationTops.clear();
      this.getData(true);
    }
  }

  public onTablePlotSelect(e) {
    this.tablePlotSelected = e.value;
    this.formationTopsStore.update((state) => ({
      ...state,
      tablePlotSelected: e.value
    }));
    this.getData(true);
  }

  public onNorthingUdtSelect(e) {
    this.northingUdtSelected = e.value;
    this.formationTopsStore.update((state) => ({
      ...state,
      northingUdtSelected: e.value
    }));
    this.plotName = 'formationTops ' + this.northingUdtSelected;
    this.plotFormation(this._depths, this._northing);
  }

  public onFormationContextMenu(row: UntypedFormGroup): void {
    const index = this.formationTops.controls.indexOf(row);
    this.contextMenuItems.set([
      {
        label: 'Insert row above',
        icon: PrimeIcons.ARROW_UP,
        disabled: this.userRoles.readOnly || this.formationTops.invalid || index <= 0,
        command: () => this.onAddFormation(this.formationTops.controls.indexOf(row))
      }
    ]);
  }

  public async onAddFormation(index: number = this.formationTops.length): Promise<void> {
    if (this.userRoles.readOnly || this.formationTops.invalid || index < 0 ||
        index > this.formationTops.length || (index === 0 && this.formationTops.length > 0)) {
      return;
    }
    const row = this.newFormation(true, index);
    if (index < this.formationTops.length) {
      const previousDepth: number = this.formationTops.at(index - 1).getRawValue().formationTop;
      const nextDepth: number = this.formationTops.at(index).getRawValue().formationTop;
      row.patchValue({ formationTop: previousDepth + (nextDepth - previousDepth) / 2 }, { emitEvent: false });
    }
    this.formationTops.insert(index, row);
    row.get('formationTop').updateValueAndValidity({ emitEvent: false });

    await this.handleSaveFormationRow(row.getRawValue(), row);
  }

  public onDeleteFormation(idx: number): void {
    const formationId = this.formationTops.controls[idx].value.id;
    if (formationId.length < 36) {
      this.formationTops.removeAt(idx)
    } else {
      this._confirmationService.confirm({
        message: 'Are you sure that you want to delete the selected formation section?',
        accept: async () => {
          if (formationId.length > 24) { // it's been saved to the db, so delete through API
            await lastValueFrom(this._formationTopService.deleteFormation(formationId));
            this._messenger.publish(new DataSavedMessage('Formation Tops'));
            this.getData(false);
          } else {
            this._formationTops = this._formationTops.filter(val => val.id !== formationId);
          }
        }
      });
    }
  }

  getData(isSignalR?: boolean) {
    const sources: Observable<any>[] = [
      this._trajectoryService.getTrajectoryPoints() as Observable<any>,
      this._wellTypeService.getWellType() as Observable<any>,
      this._formationsService.getFormationCement() as Observable<any>,
      this._formationTopService.getAllFormationTops() as Observable<any>,
      this.udtService.getUndisturbedTemperaturePlot() as Observable<any>
    ];

    forkJoin(sources).pipe(
      map(([trajectory, wellType, formations, formationTops, udt]) => {
        const waterDepth = wellType.waterDepth ? wellType.waterDepth : 0;
        this.mudlineDepth = +(wellType.drillFloorElevation + waterDepth).toFixed(2);
        this.totalDepth = trajectory[trajectory.length - 1].trueVerticalDepth;
        this._formations = formations.filter(x => x.discriminator == 'formation').sort((a, b) => a.name.localeCompare(b.name));
        this._formationTops = formationTops;
        this._udtPlotData = udt;
        this.defaultFormation = this._formations.find(x => x.name == 'Default') ?? this._formations[0];
        const depths = map1(trajectory, 'trueVerticalDepth');
        const northing = map1(trajectory, 'northing');

        if (!isSignalR) {
          this.populateFormData(isSignalR);
        }
        this._depths = depths;
        this._northing = northing;
        this.plotFormation(depths, northing);

        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  private plotFormation(depths, northing) {
    this.plot.data = [];
    let minX;
    let maxX;
    if (this.northingUdtSelected == 'northing') {
      minX = Math.min(...northing) - 230;
      maxX = Math.max(...northing) + 230;
    }
    else {
      minX = Math.min(...this._udtPlotData.data.x) - 20;
      maxX = Math.max(...this._udtPlotData.data.x) + 20;
    }

    const formationColors: string[] = [
      '#743E07',
      '#9C560F',
      '#D27617',
      '#8E5D2A',
      '#6D4F31',
      '#A36A2F',
      '#EC8316',
      '#A96F34'
    ];

    let i = 0;
    this._formationTops.forEach(formTop => {
      const top = {
        name: formTop.formation['name'] + ' Top',
        y: [formTop.formationTop, formTop.formationTop],
        x: [minX - 0.5, maxX + 0.5],
        mode: 'lines',
        hoverinfo: 'none',
        line: {
          color: formationColors[i]
        },
      }
      i++;
      if (i >= formationColors.length) { i = 0; }
      this.plot.data.push(top);
    });

    if (this.northingUdtSelected == 'northing') {
      const trajectory = {
        name: "Northing",
        y: depths,
        x: northing,
        line: {
          color: '#017BEE'
        },
        mode: 'lines'
      }

      this.plot.data.push(trajectory);
    } else {
      const udtData = {
        name: 'UDT',
        x: this._udtPlotData.data.x,
        y: this._udtPlotData.data.y,
        line: {
          color: '#FF0000'
        },
      }

      this.plot.data.push(udtData);
    }

    this.xAxisTitle = this.northingUdtSelected == 'northing' ? `Northing (${this.uu.longLengths})` : `UDT (${this.uu.temperature})`;
    this.yAxisTitle = `TVD (${this.uu.longLengths})`;

    this.isLoading.set(false);
  }

  getPlaceholderText(data: any) {
    if (data == null) {
      return 'Select Formation';
    }

    const formationExists = this._formations.findIndex(x =>
      x.density === data.density &&
      x.poissonsRatio === data.poissonsRatio &&
      x.specificHeatCapacity === data.specificHeatCapacity &&
      x.thermalConductivity === data.thermalConductivity &&
      x.thermalExpansionCoefficient === data.thermalExpansionCoefficient &&
      x.youngsModulus === data.youngsModulus
  ) >= 0;

    return formationExists ? data.name : data.name + ' (local only)';
  }

  public showJson(data: any) {
    return JSON.stringify(data, null, '\t');
  }

  private populateFormData(isSignalR: boolean) {
    this.formationTops.clear();
    let formationTop: { id: any, formationTop: any, formation: any }[];

    if (this._formationTops.length === 0) {
      const defaultFormationTop: FormationTop = {
        formation: this.defaultFormation,
        formationTop: this.mudlineDepth,
        id: Utilities.generateFakeGuid()
      }

      this._formationTopService.setFormationTop(defaultFormationTop).subscribe(() => {
        this._formationTopService.getAllFormationTops().subscribe(res => {
          this._formationTops = res;
          this.populateFormData(isSignalR);
        });
      });
    }

    this._formationTops.forEach((data, idx) => {
      const formationTopFg = this.newFormation(false, idx);
      if (!isSignalR) {
        this.formationTops.push(formationTopFg);
      }
      const expandedData = { ...data };
      formationTop = [{ id: expandedData.id, formationTop: expandedData.formationTop, formation: expandedData.formation }];
      this.formationTops?.controls[idx]?.setValue(formationTop[0], { emitEvent: false });
    });
  }

  private newFormation(isNewFormation: boolean, idx: number): UntypedFormGroup {
    const defaultDepth = this.formationTops.getRawValue()[this.formationTops.value.length - 1]?.formationTop + 100 || this.mudlineDepth;
    const formationTopFg = new UntypedFormGroup({
      id: new UntypedFormControl(Utilities.generateFakeGuid()),
      formationTop: new UntypedFormControl({ value: defaultDepth, disabled: this.userRoles.readOnly || idx == 0 }, { validators: [Validators.required, Validators.min(this.mudlineDepth), Validators.max(this.totalDepth), valuesIncreaseValidator("formationTop")], updateOn: 'blur' }),
      formation: new UntypedFormControl({ value: this._formations ? this.defaultFormation : null, disabled: this.userRoles.readOnly }, Validators.required),
    });

    this._subscriptions.add(formationTopFg.valueChanges.subscribe((v) => this.handleSaveFormationRow(v, formationTopFg)));

    if (isNewFormation && this.formationTops.value.length < 1) {
      formationTopFg.controls.formationTop.patchValue(this.mudlineDepth);
    }

    formationTopFg.markAllAsTouched();

    return formationTopFg;
  }

  private async handleSaveFormationRow(v: any, formationTopRecord?: UntypedFormGroup): Promise<void> {
    if (v.formationTop < this.mudlineDepth || v.formationTop > this.totalDepth) {
      return;
    }
    if (formationTopRecord?.valid ?? true) {
      if (!v.formationTop) {
        const rawData = this.formationTops.getRawValue();
        v = rawData.find(x => x.id == v.id);
      }
      const savedRow = this._formationTops.find(row => row.id === v.id);
      if (savedRow && savedRow.formationTop === v.formationTop && isEqual(savedRow.formation, v.formation)) {
        return;
      }
      if (v.id.length == 24) { // it's only on the client so save to db as a new record
        const saved = await lastValueFrom(this._formationTopService.setFormationTop(new FormationTop(v)));
        const idx = this.formationTops.getRawValue().findIndex(x => x.id == v.id);
        this.formationTops.at(idx).patchValue({ id: saved.id }, { emitEvent: false });
        this._formationTops = [...this._formationTops, new FormationTop({ ...v, id: saved.id })]
          .sort((a, b) => a.formationTop - b.formationTop);
      } else {
        await lastValueFrom(this._formationTopService.editFormationTop(new FormationTop(v)));
        this._formationTops = this._formationTops.map(row =>
          row.id === v.id ? new FormationTop(v) : row
        );
      }
      this._messenger.publish(new DataSavedMessage('Formation Tops'));
    }
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }

}
